import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { cn } from "../../utils/bem";
import { searchSettings } from "./settingsIndex";
import "./SettingsSearch.prefix.css";

const block = cn("settings-search");

// Instance-unique id prefix — the settings sidebar mounts the search box
// twice (desktop rail + mobile select container), and duplicated DOM
// ids would break the combobox's aria-activedescendant contract.
let _searchInstance = 0;

/**
 * "Find a setting" box at the top of Project Settings. First-time users
 * don't know which tab holds "how many annotators per task" — they type
 * what they want in their own words and jump straight to the right tab.
 * Matching runs over a hand-kept synonym index (settingsIndex.js).
 */
export const SettingsSearch = ({ base }) => {
  const history = useHistory();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef(null);
  const idPrefix = useRef(`settings-search-${++_searchInstance}`).current;

  const results = useMemo(() => searchSettings(query), [query]);
  const open = focused && results.length > 0;

  useEffect(() => setCursor(0), [query]);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setFocused(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const go = (entry) => {
    setQuery("");
    setFocused(false);
    history.push(`${base}${entry.path === "/" ? "" : entry.path}`.replace(/\/+$/, "") || base);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[cursor]);
    } else if (e.key === "Escape") {
      setFocused(false);
    }
  };

  return (
    <div ref={rootRef} className={block.toClassName()}>
      <input
        type="search"
        className={block.elem("input").toClassName()}
        placeholder="Find a setting… e.g. annotators per task"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onKeyDown={onKeyDown}
        aria-label="Find a setting"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${idPrefix}-results`}
        aria-activedescendant={open ? `${idPrefix}-opt-${cursor}` : undefined}
        aria-autocomplete="list"
      />
      {open && (
        <div id={`${idPrefix}-results`} className={block.elem("results").toClassName()} role="listbox">
          {results.map((entry, i) => (
            <div key={`${entry.path}-${entry.label}`}>
              <button
                type="button"
                role="option"
                id={`${idPrefix}-opt-${i}`}
                aria-selected={i === cursor}
                className={block
                  .elem("result")
                  .mod({ active: i === cursor })
                  .toClassName()}
                onMouseEnter={() => setCursor(i)}
                onClick={() => go(entry)}
              >
                <span className={block.elem("result-label").toClassName()}>{entry.label}</span>
                <span className={block.elem("result-tab").toClassName()}>{entry.tab}</span>
              </button>
            </div>
          ))}
        </div>
      )}
      {focused && query.trim().length >= 2 && results.length === 0 && (
        <div className={block.elem("noresults").toClassName()}>No match — browse the sections below.</div>
      )}
    </div>
  );
};
